import * as Sentry from '@sentry/react';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import i18n from './core/i18n/i18n';
import { Router } from './Router';

// styles
import 'sanitize.css';
import './core/styles/form.scss';
import './core/styles/global.scss';
import './core/styles/grid.scss';
import './core/styles/modal.scss';
import './core/styles/styles.scss';
import './core/styles/table.scss';
import './core/styles/typography.scss';
import './core/styles/mixins/paddingMarginMixin.scss';
import './core/styles/board.scss';

// yup functions
import './globals/yup.custom';

// dayjs
import './setupDayjs';

// Sentry instrumentation
if (import.meta.env.VITE_APP_SENTRY_DSN !== 'false') {
  Sentry.init({
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    environment: import.meta.env.VITE_APP_ENVIRONMENT,
    release: __SENTRY_RELEASE__,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
      Sentry.browserProfilingIntegration(),
    ],
    tracesSampleRate: 0.1,
    profilesSampleRate: 0.1,
    tracePropagationTargets: [import.meta.env.VITE_APP_BACKEND_URI, 'localhost'],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 0.3,
    sendDefaultPii: false,
  });
}

// PO-Feedback-Overlay: nur in Testumgebungen (VITE_APP_FEEDBACK=true), landet als eigener Chunk
// im Build und wird in allen anderen Umgebungen nie geladen.
if (import.meta.env.VITE_APP_FEEDBACK === 'true') {
  import('./components/FeedbackOverlay/mountFeedbackOverlay').then(({ mountFeedbackOverlay }) => {
    mountFeedbackOverlay();
  });
}

const container = document.getElementById('root');
const root = createRoot(container as Element);

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <Router />
      </I18nextProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
